// Calibration.tsx
import React, { useState, useMemo, useEffect } from 'react';
import Text from '../text';
import dayjs from 'dayjs';
import CalibTextField from '../text-field/CalibTextField';
import TextField from '@mui/material/TextField';
import {
	sendCommand,
	responseEmitter,
} from '../../utils/usb/USBConnector';
import LoadingPopup from '../dialog/LoadingBar';
import { useConnection } from '../../utils/helper/ConnectionContext';
import 'react-toastify/dist/ReactToastify.css';
import {
	getCalibrationResult,
	saveCalibrationResult,
	cleanupCalibrationData,
} from '../../utils/services/idbService';
import { formatNumber } from 'utils/functions/formatNumber';
import { TestSelection } from '../../types/testSelection';
import { isBehindCalibration } from 'utils/functions/testAndEditHelpers';
import ReadButton from '../button/ReadButton';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import CONSTANTS from '../../utils/constants/UIConstants';

interface Location {
	id: number;
	name: string;
	testKitDescriptors: {
		testKitName: string;
		id: number;
	}[];
}

interface TestKitOutputDescriptor {
	id: number;
	outputName: string;
	outputId: string;
	unit: string;
	maxVal: number;
	minVal: number;
	toPrev: boolean;
	formula: string;
	hide: boolean;
	hiAlarmValue?: number;
	hiHiAlarmValue?: number;
	loAlarmValue?: number;
	loLoAlarmValue?: number;
	// ... add other fields as necessary
}

interface CalibrationProps {
	addStyle?: string;
	locations?: Location[];
	onInputChange?: (
		inputId: string,
		value: number,
		updatedMissingFields: string[]
	) => void;
	missingFields: string[];
	selectionData?: TestSelection[] | null;
	siteId: number;
}

interface TestKitInputDescriptor {
	id: number;
	inputId: string;
	inputName: string;
	unit: string;
	minimum: number;
	toPrev: boolean;
	readPM: boolean;
	isRequired: boolean;
	defaultValue: number;
}

const Calibration: React.FC<CalibrationProps> = ({
	addStyle = '',
	locations = [],
	onInputChange,
	missingFields,
	selectionData,
	siteId,
}) => {
	const [selectedDate, setSelectedDate] = useState<string>(
		dayjs().format('YYYY/MM/DD - h:mm A')
	);
	const [value, setValue] = useState('');
	const SAMPLE_SIZE = 40;
	const [progress, setProgress] = useState<number>(0);
	const [deviceStatus, setDeviceStatus] = useState<"starting" | "reading" | "completed" | undefined>('starting');

	const cleanupMemory = (handleData: any) => {
		responseEmitter.removeListener('data', handleData);
		setProgress(0);
		setShowDialog(false);
		setDeviceStatus('starting');
		if(clearTimeoutId !== null){
			clearTimeout(clearTimeoutId);
			clearInterval(clearTimeoutId);
			setClearTimeoutId(null);
		}
	}

	const fetchedDetails = useMemo(() => {
		const details: any[] = [];
		for (const location of locations ?? []) {
			for (const testKit of location.testKitDescriptors ?? []) {
				const alreadyExists = details.some(
					(fetchedTestKit) =>
						JSON.stringify(fetchedTestKit) ===
						JSON.stringify(testKit)
				);

				if (!alreadyExists) {
					details.push(testKit);
				}
			}
		}
		return details;
	}, [locations]);

	function determineKitWithMostCalibrations(kits: TestSelection[]) {
		let maxCalibrations = -1;
		let returnKit = null;
		for (const kit of kits) {
			const testKitDetail = fetchedDetails.find(
				(tk) => tk.testKitName === kit.testKit
			);
			const calibrationCount = testKitDetail
				? testKitDetail.testKitInputDescriptorVOs.filter(
						(descriptor: any) =>
							descriptor?.type?.includes('Calibration') ||
							isBehindCalibration(testKitDetail.testKitInputDescriptorVOs, descriptor)
				  ).length
				: 0;
			if (calibrationCount > maxCalibrations) {
				maxCalibrations = calibrationCount;
				returnKit = kit;
			}
		}
		return returnKit;
	}

	//Determine calibrations based on all test kits currently added to the batch
	//for now, logic will be to look for whichever kit has the most calibrations
	const kitWithMostCalibrations = determineKitWithMostCalibrations(selectionData ?? []);

	const selectedTestKit = kitWithMostCalibrations?.testKit
		? fetchedDetails.find((tk) => tk.testKitName === kitWithMostCalibrations.testKit)
		: fetchedDetails[0];
	const testKitInputDescriptorVOs = selectedTestKit?.testKitInputDescriptorVOs.sort((a: any, b: any) => (a.sortorder ?? 0) - (b.sortorder ?? 0)) ?? [];

	const [inputValues, setInputValues] = useState<Record<string, number>>({});
	const [showDialog, setShowDialog] = useState(false);
	const { isConnected, setIsConnected } = useConnection();
	const [clearTimeoutId, setClearTimeoutId] = useState<number | null>(null);
	const [prevConnectStatus, setPrevConnectStatus] = useState(false);
	
	if (isConnected != null && isConnected != prevConnectStatus) {
		setPrevConnectStatus(!prevConnectStatus);
	}

	//Update both progressBars based off of current number out of 40(should be this many sample for a full upload)
	const updateProgressBar = async (currProgress: number) => {
		//Prevent jumping if multiple are running at the same time and other conditions haven't caught it.
		if(progress <= currProgress){
			let progressPercent = Math.min((currProgress / SAMPLE_SIZE) * 100, 100);
			setProgress(prev => progressPercent);
			setDeviceStatus(prev => 'reading');
		}
	};
	const updateInputValue = async (descriptor: any, inputValue: any) => {
		const newInputValues = {
			...inputValues,
			[descriptor.inputId]: inputValue,
		};

		// Original onChange logic
		if (onInputChange) {
			let updatedMissingFields = [...missingFields];

			if (!inputValue) {
				// If field is left empty
				if (!updatedMissingFields.includes(descriptor.inputId)) {
					updatedMissingFields.push(descriptor.inputId);
				}
			} else {
				updatedMissingFields = updatedMissingFields.filter(
					(fieldId) => fieldId !== descriptor.inputId
				);
			}
			setInputValues(newInputValues);
			onInputChange(descriptor.inputId, inputValue, updatedMissingFields);
		}

		// Update the selectedDate when a calibration field changes
    const username = localStorage.getItem('currentUser') ?? 'unknown';
    const calibrationValue = await getCalibrationResult(
      descriptor.inputId,
      username
    );
    const prevValue = calibrationValue?.calibrationData;
		if (prevValue !== inputValue) {
			setSelectedDate(dayjs().format('YYYY/MM/DD - h:mm A'));
		
		if (
			descriptor?.type?.includes('Calibration') ||
			isBehindCalibration(testKitInputDescriptorVOs, descriptor)
		) {
			const username = localStorage.getItem('currentUser') ?? 'unknown';
			await saveCalibrationResult(
				descriptor.inputId,
				inputValue,
				username
			);
		}
  }
	};
	const calibrationFields = useMemo(() => {
		return (testKitInputDescriptorVOs ?? [])
			.filter(
				(descriptor: any) =>
					descriptor?.type?.includes('Calibration') ||
					isBehindCalibration(testKitInputDescriptorVOs, descriptor)
			)
			.map((descriptor: TestKitInputDescriptor) => {
				const isError =
					missingFields.includes(descriptor.inputId) &&
					inputValues[descriptor.inputId] === null;

				return (
					<div className="flex space-x-2 items-center !mb-3">
						<TextField
							key={descriptor.id}
							label={descriptor.inputName}
							required={descriptor.isRequired}
							type="number"
							size="small"
							error={isError}
							value={
								inputValues[descriptor.inputId] !== undefined
									? inputValues[descriptor.inputId]
									: descriptor?.defaultValue
							}
							defaultValue={descriptor?.defaultValue}
							onChange={(e) => handleFieldChange(descriptor, e)}
							sx={{
								'.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
									borderRadius: '2px',
								},
								'.MuiFormLabel-root': {
                                    backgroundColor: inputValues[descriptor.inputId] !== undefined && inputValues[descriptor.inputId] !== null
										? CONSTANTS.TEXT_INPUT_LABEL_BACKGROUND : 'transparent',
                                    maxWidth: descriptor.inputName.length >= 10 ? '130%' : '100%',
                                    padding: '0.25rem 0.35rem 0 0.35rem',
                                    marginLeft: '-0.35rem',
                                    marginTop: '-0.10rem',
                                    borderRadius: '5px',
                                },
                                '.MuiOutlinedInput-root': {
                                    backgroundColor: CONSTANTS.TEXT_INPUT_LABEL_BACKGROUND,
								},
								'& .MuiOutlinedInput-input': {
										padding: '8.5px 14px',
									},
							}}
							inputProps={{
								style: {
									backgroundColor: 'white',
									width: '172px',
								},
							}}
							InputLabelProps={{
								shrink: inputValues[descriptor.inputId] !== undefined && inputValues[descriptor.inputId] !== null,
							}}
						/>
						{ descriptor.readPM && (<ReadButton
							descriptor={descriptor}
							isConnected={isConnected}
							siteId={siteId}
							currProgress={progress}
							updateProgressBar={updateProgressBar}
							updateInputValue={updateInputValue}
							cleanupMemory={cleanupMemory}
							setShowDialog={setShowDialog}
							setTimer={(timeout?: number) => setClearTimeoutId(timeout ?? null)}
						/>)}
					</div>
				);
			});
	}, [testKitInputDescriptorVOs, onInputChange, missingFields, inputValues, prevConnectStatus]);

	

	const handleFieldChange = (
		descriptor: TestKitInputDescriptor,
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const value = e.target.value;
		let inputValue;

		if (value !== '') {
			inputValue = parseFloat(value);

			if (isNaN(inputValue)) {
				inputValue = null;
			}
		} else {
			inputValue = null;
		}

		updateInputValue(descriptor, inputValue);
	};

	useEffect(() => {
		const fetchCalibrationValues = async () => {
			const descriptors = [...testKitInputDescriptorVOs];
			const newInputValues = { ...inputValues };
			for (const descriptor of descriptors) {
				if (
					descriptor?.type?.includes('Calibration') ||
					isBehindCalibration(descriptors, descriptor)
				) {
					const username =
						localStorage.getItem('currentUser') ?? 'unknown';
					const calibrationValue = await getCalibrationResult(
						descriptor.inputId,
						username
					);
					if (calibrationValue !== null && calibrationValue.calibrationData !== null && calibrationValue.timestamp !== null) {
						newInputValues[descriptor.inputId] = calibrationValue.calibrationData;
                        updateInputValue(descriptor, calibrationValue.calibrationData);
						setSelectedDate(dayjs.unix(calibrationValue.timestamp/1000).format('YYYY/MM/DD - h:mm A'));
					}
				}
			}

			setInputValues(newInputValues);
		};

		fetchCalibrationValues();
	}, [siteId, testKitInputDescriptorVOs]);

	useEffect(() => {
		cleanupCalibrationData();
	}, []);
	const handleClose = () => {
		setShowDialog(false);
	};
	return (
		<div className="flex justify-center calibration">
			<div
				className={`bg-primary-lu-blue-100 w-[1108px] flex px-2 py-2 ${addStyle} border-b border-gray-300`}
			>
				<div className="flex-1 px-4 pt-3 pb-2 text-left  calibration-text">
					<Text addStyle="!font-inter !text-[20px] !font-semibold !leading-[32px] !tracking-[0.150px] !mb-2">
						Calibration
					</Text>
					{calibrationFields.length > 2 ? (
						<>
							<Text addStyle="!mb-4 !font-jost !text-[14px] !font-normal !leading-[18px] !w-[320px]">
								For each testing event (i.e. all testing done on
								a single day, or each set of samples tested at a
								given site), perform the following three
								calibrations.
							</Text>
							<Text addStyle="!mb-4 !font-jost !text-[14px] !font-normal !leading-[18px] !w-[320px]">
								Be sure to warm ATP Enzyme and AMP Enzyme to
								room temperature prior to use.
							</Text>
							<Text addStyle="!mb-4 !font-jost !text-[14px] !font-normal !leading-[18px] !w-[320px]">
								Calibration measurements will be applied to all
								applicable tests run within the next two hours.
							</Text>
						</>
					) : (
						calibrationFields.length < 3 && (
							<Text addStyle="!mb-4 !font-jost !text-[14px] !font-normal !leading-[18px] !w-[320px]">
								Calibration measurements will be applied to
								all applicable tests run within the next two
								hours.
							</Text>
						)
					)}
				</div>
				<div className="flex-1 px-5 pt-3 pb-2 text-left  calibration-input">
					<div className="flex items-center space-x-2 mb-5 ml-2 calibration-date-time">
						<img
							src={'/assets/icons/Calendar.svg'}
							alt="Calender Icon"
							className="w-5 h-5 md:block hidden"
						/>
						<Text addStyle="!text-[#556670] !font-inter !text-[14px]">
							{selectedDate}
						</Text>
					</div>

					{calibrationFields}
				</div>
				<div className="flex-1 px-4 pt-3 pb-2 text-left  calibarion-results">
					{calibrationFields.length > 2 && (
						<>
							<Text addStyle="!font-inter !text-[20px] !font-semibold !leading-[32px] !tracking-[0.150px] !mb-4">
								Results
							</Text>
							<CalibTextField
								label="LAAF"
								value={
									isNaN(
										inputValues['RLUAMP1'] /
											inputValues['RLUATP2']
									)
										? ''
										: formatNumber(inputValues['RLUAMP1'] /
										  inputValues['RLUATP2'])
								}
								onChange={(e) => setValue(e.target.value)}
								disabled={true}
								addStyle="!w-[154px] LAAF-result"
							/>
							<Text addStyle="!mb-4 !font-inter !text-[12px] !font-normal !ml-2 !mt-2 !text-primary-grey">
								If LAAF &lt; 0.3, replace AMP Enzyme.
							</Text>
						</>
					)}
				</div>
			</div>
			{showDialog && <LoadingPopup open={showDialog} progress={progress} type={deviceStatus} onClose={handleClose} />}
		</div>
	);
};

export default Calibration;
